import './CustomerView.scss';
import { AsyncStateNotification } from '@ui/notifications';
import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { CustomerViewExtra } from './CustomerViewExtra';
import { CustomerViewTitle } from './CustomerViewTitle';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { ShardCatalogSearch } from '../../ShardCatalogSearch';
import { ADMIN_DASHBOARD_COPY, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { adminActions } from '@sonar-software/sonar-admin';
import { Card, Collapse } from 'antd';
import { ConfigurationPanel, SalesforceSetupPanel, UsersPanel } from './panels';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';
const defaultShardCatalogDetailSelectors = createAsyncSelectors((state) => state.admin.search.shardCatalog.details.default.get);
const getCustomerSelectors = createAsyncSelectors((state) => state.core.customers.byId.get);
const patchCustomerSelectors = createAsyncSelectors((state) => state.core.customers.byId.patch);
const postLockAllUsersSelectors = createAsyncSelectors((state) => state.admin.customers.byId.lockAllUsers.post);
export const CustomerView = () => {
    const [selectedSearchDetails, setSelectedSearchDetails] = useState(undefined);
    const patchCustomerIsReceived = useSonarSelector(patchCustomerSelectors.isReceived);
    const defaultShardCatalogDetail = useSonarSelector(defaultShardCatalogDetailSelectors.data);
    const getDefaultShardCatalogDetail = useAction(adminActions.requestGetAdminSearchShardCatalogDetailsDefault);
    const customer = useSonarSelector(getCustomerSelectors.data);
    const getCustomer = useAction(coreActions.requestGetCoreCustomersById);
    const resetCustomer = useAction(coreActions.resetGetCoreCustomersById);
    const getCustomerForSearch = useCallback((details) => {
        if (details) {
            getCustomer({
                customerId: details.customerId,
                shardName: details.shardName
            });
        }
    }, [getCustomer]);
    const handleSearchDetailsChanged = useCallback((details) => {
        var _a;
        resetCustomer();
        setSelectedSearchDetails((_a = details !== null && details !== void 0 ? details : defaultShardCatalogDetail) !== null && _a !== void 0 ? _a : undefined);
        if (details) {
            getCustomerForSearch(details);
        }
    }, [defaultShardCatalogDetail, getCustomerForSearch, resetCustomer]);
    useEffect(() => {
        if (!defaultShardCatalogDetail) {
            getDefaultShardCatalogDetail();
        }
    }, [getDefaultShardCatalogDetail, defaultShardCatalogDetail]);
    useEffect(() => {
        if (!selectedSearchDetails && defaultShardCatalogDetail) {
            setSelectedSearchDetails(defaultShardCatalogDetail);
            getCustomer(defaultShardCatalogDetail);
        }
    }, [defaultShardCatalogDetail, getCustomer, selectedSearchDetails]);
    useEffect(() => {
        if (patchCustomerIsReceived && selectedSearchDetails) {
            getCustomerForSearch(selectedSearchDetails);
        }
    }, [patchCustomerIsReceived, resetCustomer, getCustomerForSearch, selectedSearchDetails]);
    const isLoading = !selectedSearchDetails || !customer;
    const title = React.createElement(CustomerViewTitle, { customer: customer, shardName: selectedSearchDetails === null || selectedSearchDetails === void 0 ? void 0 : selectedSearchDetails.shardName });
    const extra = React.createElement(CustomerViewExtra, { customer: customer, shardName: selectedSearchDetails === null || selectedSearchDetails === void 0 ? void 0 : selectedSearchDetails.shardName });
    return (React.createElement(LoadingSpinner, { spinning: isLoading },
        React.createElement(ShardCatalogSearch, { id: 'search-shard-catalog', onDetailsChanged: handleSearchDetailsChanged }),
        React.createElement(Card, { className: 'admin-customer-view-card', extra: extra, title: title, variant: 'borderless' },
            React.createElement(Collapse, { defaultActiveKey: ['1', '2', '3'], items: [
                    {
                        children: React.createElement(UsersPanel, { searchDetails: selectedSearchDetails }),
                        key: '1',
                        label: COPY_SPECIFIC.USERS
                    }, {
                        children: React.createElement(SalesforceSetupPanel, { searchDetails: selectedSearchDetails }),
                        key: '2',
                        label: COPY_SPECIFIC.SALESFORCE_SETUP
                    }, {
                        children: React.createElement(ConfigurationPanel, { customer: customer, searchDetails: selectedSearchDetails }),
                        key: '3',
                        label: COPY_SPECIFIC.CONFIGURATION
                    }
                ] })),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.UPDATE_CUSTOMER_FAILED, failed: ADMIN_DASHBOARD_COPY.UPDATE_CUSTOMER_FAILED, stateSelector: patchCustomerSelectors.state }),
        React.createElement(AsyncStateNotification, { errored: ADMIN_DASHBOARD_COPY.LOCK_ALL_USERS_FAILED, failed: ADMIN_DASHBOARD_COPY.LOCK_ALL_USERS_FAILED, stateSelector: postLockAllUsersSelectors.state })));
};
